import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Footer } from './shared/ServicePageSections';
import ArrowIcon from '../components/ArrowIcon';
import {
  PROJECTS,
  PROJECT_SECTORS,
  PROJECT_IMPACT,
  PROJECT_TECH,
  projectsBySector,
} from '../data/projectsData';

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */

function useRevealOnce(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function useCount(target, active, duration = 1700) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf, start = null;
    const tick = ts => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return val;
}

/* Pointer devices only — the index preview panel is meaningless on touch. */
function useHasHover() {
  const [has, setHas] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const on = () => setHas(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return has;
}

function useIsNarrow(px = 860) {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= px : false
  );
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth <= px);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [px]);
  return narrow;
}

const hexA = (hex, a) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

/* ═══════════════════════════════════════════════════════════
   HERO — telemetry console visual
   A purely decorative "live deployment" panel. No real data:
   it exists to make the hero feel like the systems we ship.
═══════════════════════════════════════════════════════════ */

const CONSOLE_ROWS = [
  { site: 'NICU-BLR-03',   sector: 'Healthcare', color: '#C0392B', v: 62,  unit: 'beds' },
  { site: 'SOLAR-PLT-06',  sector: 'Energy',     color: '#E67E22', v: 48,  unit: 'MW' },
  { site: 'PLANT-LINE-12', sector: 'Industrial', color: '#5D4037', v: 142, unit: 'machines' },
  { site: 'CAMPUS-LAB-04', sector: 'Education',  color: '#1565A7', v: 4,   unit: 'labs' },
];

function TelemetryConsole() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 2400);
    return () => clearInterval(t);
  }, []);

  // Deterministic pseudo-random sparkline that shifts on each tick.
  const points = useMemo(() => {
    const n = 34;
    return Array.from({ length: n }, (_, i) => {
      const s = Math.sin((i + tick * 2) * 0.55) * 0.5
              + Math.sin((i + tick * 2) * 0.21) * 0.32
              + Math.sin((i + tick) * 1.31) * 0.14;
      return { x: (i / (n - 1)) * 100, y: 50 - s * 30 };
    });
  }, [tick]);

  const path = points.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
  const area = `${path} L100 100 L0 100 Z`;

  return (
    <div
      style={{
        background: 'linear-gradient(160deg, #0E0E1C 0%, #0A0A14 60%, #12101F 100%)',
        borderRadius: 22,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 40px 90px rgba(10,10,20,0.32), 0 0 0 1px rgba(255,255,255,0.03) inset',
        padding: '20px 20px 18px',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {/* grid texture */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.026) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.026) 1px, transparent 1px)',
        backgroundSize: '26px 26px',
      }} />
      {/* accent glow */}
      <div aria-hidden style={{
        position: 'absolute', top: '-30%', right: '-18%', width: '65%', height: '70%',
        background: 'radial-gradient(ellipse, rgba(0,60,255,0.30) 0%, transparent 62%)', pointerEvents: 'none',
      }} />
      <div aria-hidden style={{
        position: 'absolute', bottom: '-25%', left: '-14%', width: '55%', height: '55%',
        background: 'radial-gradient(ellipse, rgba(255,0,4,0.16) 0%, transparent 65%)', pointerEvents: 'none',
      }} />
      {/* scan line */}
      <div aria-hidden style={{
        position: 'absolute', left: 0, right: 0, height: 1, zIndex: 1, pointerEvents: 'none',
        background: 'linear-gradient(90deg, transparent 8%, rgba(0,120,255,0.75) 50%, transparent 92%)',
        animation: 'pjScan 6s ease-in-out 1s infinite',
      }} />

      <div style={{ position: 'relative', zIndex: 2 }}>
        {/* title bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF4444' }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#FFB800' }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00C853' }} />
          <span style={{ flex: 1, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.26)' }}>
            deployments.live
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#22C55E' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22C55E', animation: 'redPulse 2.2s ease-in-out infinite' }} />
            LIVE
          </span>
        </div>

        {/* sparkline */}
        <div style={{ position: 'relative', height: 84, marginBottom: 14, borderRadius: 12, overflow: 'hidden', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
            <defs>
              <linearGradient id="pjSparkFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B6BFF" stopOpacity="0.42" />
                <stop offset="100%" stopColor="#3B6BFF" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="pjSparkLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#FF0048" />
                <stop offset="55%" stopColor="#6B7BFF" />
                <stop offset="100%" stopColor="#00D4FF" />
              </linearGradient>
            </defs>
            <path d={area} fill="url(#pjSparkFill)" style={{ transition: 'd 1200ms cubic-bezier(.4,0,.2,1)' }} />
            <path d={path} fill="none" stroke="url(#pjSparkLine)" strokeWidth="1.4" vectorEffect="non-scaling-stroke"
                  strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'd 1200ms cubic-bezier(.4,0,.2,1)' }} />
          </svg>
          <div style={{ position: 'absolute', top: 8, left: 11, fontFamily: "'JetBrains Mono', monospace", fontSize: 8.5, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.3)' }}>
            INGEST · events/sec
          </div>
        </div>

        {/* rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, borderRadius: 12, overflow: 'hidden', background: 'rgba(255,255,255,0.05)' }}>
          {CONSOLE_ROWS.map((r, i) => (
            <div key={r.site} style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 10,
              padding: '9px 13px', background: '#0B0B16',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: r.color, boxShadow: `0 0 10px ${hexA(r.color, 0.9)}`, animation: `redPulse ${2.2 + i * 0.35}s ease-in-out ${i * 0.3}s infinite` }} />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: 'rgba(255,255,255,0.62)' }}>
                {r.site}
              </span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: 'rgba(255,255,255,0.32)', whiteSpace: 'nowrap' }}>
                <strong style={{ color: 'white', fontWeight: 600 }}>{r.v}</strong> {r.unit}
              </span>
            </div>
          ))}
        </div>

        {/* footer prompt */}
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, color: 'rgba(255,255,255,0.2)' }}>$&gt;</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, color: 'rgba(255,255,255,0.32)' }}>zx status --all</span>
          <span style={{ display: 'inline-block', width: 6, height: 11, background: 'rgba(255,255,255,0.4)', animation: 'pjBlink 1.1s step-end infinite' }} />
        </div>
      </div>
    </div>
  );
}

function HeroStat({ target, suffix, label, sub, active }) {
  const v = useCount(target, active);
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: "'Anton', sans-serif", fontWeight: 400, fontSize: 'clamp(30px,3.6vw,46px)', lineHeight: 1, color: 'var(--ink)' }}>
        {v}<span style={{ color: 'var(--red)' }}>{suffix}</span>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginTop: 7, letterSpacing: '0.01em' }}>{label}</div>
      <div style={{ fontSize: 11, color: 'rgba(10,10,20,0.4)', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FEATURED SPOTLIGHT — auto-rotating hero case
═══════════════════════════════════════════════════════════ */

function FeaturedSpotlight({ items }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [ref, visible] = useRevealOnce(0.15);

  useEffect(() => {
    if (paused || items.length < 2) return;
    const t = setInterval(() => setI(v => (v + 1) % items.length), 6500);
    return () => clearInterval(t);
  }, [paused, items.length]);

  const p = items[i];
  if (!p) return null;

  return (
    <section
      ref={ref}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{ padding: 'clamp(48px,6vw,76px) clamp(20px,5vw,48px) clamp(24px,3vw,40px)' }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* header row */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color: 'var(--blue)', textTransform: 'uppercase' }}>
              FEATURED ENGAGEMENT
            </span>
            <h2 style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(26px,3.4vw,44px)', lineHeight: 1.06, color: 'var(--ink)', marginTop: 10 }}>
              The work we&nbsp;
              <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'var(--red)' }}>point to first</span>
            </h2>
          </div>

          {/* progress dots */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 6 }}>
            {items.map((it, idx) => (
              <button
                key={it.slug}
                aria-label={`Show ${it.title}`}
                onClick={() => setI(idx)}
                className="hoverable"
                style={{
                  width: idx === i ? 32 : 8, height: 8, borderRadius: 999, padding: 0,
                  background: idx === i ? it.color : 'rgba(10,10,20,0.16)',
                  transition: 'all 320ms cubic-bezier(.2,.8,.2,1)', cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </div>

        {/* card */}
        <div
          className="pj-spotlight"
          key={p.slug}
          style={{
            display: 'grid', gridTemplateColumns: 'minmax(0,1.08fr) minmax(0,1fr)',
            background: 'white', borderRadius: 24, overflow: 'hidden',
            border: '1px solid var(--line)',
            boxShadow: `0 30px 70px rgba(10,10,20,0.12), 0 0 0 1px ${hexA(p.color, 0.16)}`,
            opacity: visible ? 1 : 0,
            transform: visible ? 'none' : 'translateY(28px)',
            transition: 'opacity 700ms cubic-bezier(.2,.8,.2,1), transform 700ms cubic-bezier(.2,.8,.2,1)',
            animation: 'pjFadeSwap 620ms cubic-bezier(.2,.8,.2,1)',
          }}
        >
          {/* visual side */}
          <div style={{ position: 'relative', minHeight: 'clamp(260px, 38vh, 440px)', overflow: 'hidden', background: '#0A0A14' }}>
            <img
              src={p.image}
              alt={p.title}
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                objectFit: 'cover', opacity: 0.86,
                animation: 'pjKenBurns 14s ease-in-out infinite alternate',
              }}
            />
            <div aria-hidden style={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(200deg, ${hexA(p.color, 0.34)} 0%, rgba(10,10,20,0.18) 42%, rgba(10,10,20,0.82) 100%)`,
            }} />
            <div aria-hidden style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
              backgroundSize: '30px 30px', mixBlendMode: 'overlay',
            }} />

            {/* top meta */}
            <div style={{ position: 'absolute', top: 18, left: 18, right: 18, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                color: 'white', background: p.color, padding: '4px 10px', borderRadius: 5,
              }}>{p.sectorName}</span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'rgba(255,255,255,0.72)',
                background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', padding: '4px 9px', borderRadius: 5,
              }}>{p.code}</span>
            </div>

            {/* bottom title */}
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(20px,2.6vw,32px)' }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.66)', marginBottom: 9 }}>
                {p.year} · {p.duration} · {p.team}
              </div>
              <h3 style={{
                fontFamily: "'Anton',sans-serif", fontWeight: 400, color: 'white',
                fontSize: 'clamp(24px,3vw,40px)', lineHeight: 1.08, marginBottom: 8,
              }}>{p.title}</h3>
              <p style={{ fontSize: 'clamp(13px,1.2vw,15px)', color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', fontFamily: "'Instrument Serif', serif" }}>
                {p.tagline}
              </p>
            </div>
          </div>

          {/* content side */}
          <div style={{ padding: 'clamp(22px,2.6vw,36px)', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 2.4, textTransform: 'uppercase', color: p.color, marginBottom: 12 }}>
              {p.client}
            </div>

            <p style={{ fontSize: 'clamp(13.5px,1.25vw,15.5px)', lineHeight: 1.78, color: 'var(--ink-soft)', marginBottom: 22 }}>
              {p.summary}
            </p>

            {/* metric grid */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 1,
              background: 'var(--line)', borderRadius: 14, overflow: 'hidden', marginBottom: 22,
            }}>
              {p.metrics.map((m, idx) => (
                <div key={m.label} style={{ background: 'white', padding: '14px 15px' }}>
                  <div style={{
                    fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(20px,2.1vw,27px)', lineHeight: 1,
                    color: idx === 0 ? p.color : 'var(--ink)',
                  }}>{m.value}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', marginTop: 6 }}>{m.label}</div>
                  <div style={{ fontSize: 10, color: 'rgba(10,10,20,0.38)', marginTop: 1 }}>{m.sub}</div>
                </div>
              ))}
            </div>

            {/* tags */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 24 }}>
              {p.tags.map(t => (
                <span key={t} style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--ink-soft)',
                  background: 'rgba(10,10,20,0.04)', border: '1px solid rgba(10,10,20,0.08)',
                  borderRadius: 5, padding: '3px 9px',
                }}>{t}</span>
              ))}
            </div>

            <div style={{ marginTop: 'auto' }}>
              <Link
                to={`/projects/${p.slug}`}
                className="hoverable"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '13px 24px', borderRadius: 999,
                  background: 'var(--ink)', color: 'white', fontSize: 13.5, fontWeight: 700,
                  boxShadow: '0 12px 30px rgba(10,10,20,0.22)',
                  transition: 'transform 220ms ease, box-shadow 220ms ease, background 220ms ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.background = p.color;
                  e.currentTarget.style.boxShadow = `0 16px 36px ${hexA(p.color, 0.4)}`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'var(--ink)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(10,10,20,0.22)';
                }}
              >
                Read the case study <ArrowIcon size={13} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   THE INDEX — numbered rows + cursor-tracking preview
═══════════════════════════════════════════════════════════ */

const PREVIEW_W = 320;
const PREVIEW_H = 216;

function IndexPreview({ project, pos, shown }) {
  /* Two layers on purpose: the outer one tracks the cursor with no transition
     (a transition here would visibly lag the pointer), the inner one owns the
     fade/scale so showing and hiding still eases.

     X is clamped into the right-hand region so the panel never sits on top of
     the left-aligned row title it is previewing; Y is clamped to the viewport. */
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
  const x = Math.min(Math.max(pos.x + 26, vw * 0.54), vw - PREVIEW_W - 24);
  const y = Math.min(Math.max(pos.y - PREVIEW_H / 2, 88), vh - PREVIEW_H - 16);

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed', top: 0, left: 0, zIndex: 60, pointerEvents: 'none',
        transform: `translate3d(${x}px, ${y}px, 0)`,
        willChange: 'transform',
      }}
    >
      {project && (
        <div style={{
          width: PREVIEW_W, height: PREVIEW_H, borderRadius: 16, overflow: 'hidden', position: 'relative',
          border: '4px solid white',
          boxShadow: `0 30px 66px rgba(10,10,20,0.28), 0 0 0 1px ${hexA(project.color, 0.35)}`,
          background: '#101020',
          opacity: shown ? 1 : 0,
          transform: shown ? 'scale(1)' : 'scale(0.9)',
          transition: 'opacity 240ms ease, transform 320ms cubic-bezier(.2,.8,.2,1)',
        }}>
          <img src={project.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.9 }} />
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(190deg, ${hexA(project.color, 0.22)} 0%, rgba(10,10,20,0.18) 45%, rgba(10,10,20,0.9) 100%)`,
          }} />
          <div style={{ position: 'absolute', left: 14, right: 14, bottom: 12 }}>
            <div style={{
              fontFamily: "'Anton',sans-serif", fontSize: 22, lineHeight: 1, color: 'white',
            }}>{project.metrics[0].value}</div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.7)', marginTop: 5 }}>
              {project.metrics[0].label}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IndexRow({ p, n, onEnter, onLeave, narrow }) {
  const [hov, setHov] = useState(false);
  const [ref, visible] = useRevealOnce(0.05);

  const enter = () => { setHov(true); onEnter(p); };
  const leave = () => { setHov(false); onLeave(); };

  return (
    <Link
      ref={ref}
      to={`/projects/${p.slug}`}
      className="hoverable pj-row"
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={leave}
      style={{
        position: 'relative', display: 'block',
        borderTop: '1px solid var(--line)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(18px)',
        transition: 'opacity 600ms cubic-bezier(.2,.8,.2,1), transform 600ms cubic-bezier(.2,.8,.2,1)',
      }}
    >
      {/* colour wash on hover */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(90deg, ${hexA(p.color, 0.08)} 0%, transparent 62%)`,
        opacity: hov ? 1 : 0, transition: 'opacity 340ms ease',
      }} />
      {/* left accent bar */}
      <div aria-hidden style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: p.color,
        transform: hov ? 'scaleY(1)' : 'scaleY(0)', transformOrigin: 'center',
        transition: 'transform 380ms cubic-bezier(.2,.8,.2,1)',
      }} />

      <div
        className="pj-row-inner"
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: narrow ? '58px 1fr auto' : '76px 1fr 190px 78px 52px',
          alignItems: 'center', gap: narrow ? 14 : 'clamp(12px,2vw,26px)',
          padding: narrow ? '16px 14px' : 'clamp(18px,2.2vw,26px) clamp(14px,2vw,26px)',
          transform: hov && !narrow ? 'translateX(10px)' : 'translateX(0)',
          transition: 'transform 380ms cubic-bezier(.2,.8,.2,1)',
        }}
      >
        {/* index number — becomes a thumbnail on touch layouts */}
        {narrow ? (
          <div style={{
            width: 58, height: 46, borderRadius: 9, overflow: 'hidden', position: 'relative',
            border: `1px solid ${hexA(p.color, 0.3)}`, background: '#101020',
          }}>
            <img src={p.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.9 }} />
            <div style={{ position: 'absolute', inset: 0, background: hexA(p.color, 0.18) }} />
          </div>
        ) : (
          <span style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: '0.1em',
            color: hov ? p.color : 'rgba(10,10,20,0.3)', transition: 'color 300ms ease',
          }}>
            {String(n).padStart(2, '0')}
          </span>
        )}

        {/* title */}
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: "'Anton',sans-serif", fontWeight: 400,
            fontSize: narrow ? 'clamp(16px,4.6vw,20px)' : 'clamp(19px,2.4vw,34px)',
            lineHeight: 1.12, color: hov ? p.color : 'var(--ink)',
            transition: 'color 300ms ease',
          }}>{p.title}</div>

          {narrow && (
            <div style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              color: 'rgba(10,10,20,0.45)', marginTop: 6,
            }}>
              {p.sectorName} · {p.year} · {p.metrics[0].value} {p.metrics[0].label.toLowerCase()}
            </div>
          )}
        </div>

        {!narrow && (
          <>
            <span style={{
              fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: hov ? p.color : 'rgba(10,10,20,0.45)', transition: 'color 300ms ease',
            }}>{p.sectorName}</span>

            <span style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
              color: 'rgba(10,10,20,0.38)',
            }}>{p.year}</span>
          </>
        )}

        {/* arrow */}
        <span style={{
          display: 'inline-flex', justifySelf: 'end',
          color: hov ? p.color : 'rgba(10,10,20,0.28)',
          transform: hov ? 'translateX(4px)' : 'translateX(0)',
          transition: 'transform 320ms cubic-bezier(.2,.8,.2,1), color 300ms ease',
        }}>
          <ArrowIcon size={narrow ? 15 : 20} />
        </span>
      </div>
    </Link>
  );
}

function WorkIndex() {
  const [filter, setFilter] = useState('all');
  const [hovered, setHovered] = useState(null);
  const [shown, setShown] = useState(false);
  const posRef = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const rafRef = useRef(0);

  const hasHover = useHasHover();
  const narrow = useIsNarrow(860);

  const list = useMemo(() => projectsBySector(filter), [filter]);
  const counts = useMemo(() => {
    const m = { all: PROJECTS.length };
    PROJECTS.forEach(p => { m[p.sector] = (m[p.sector] || 0) + 1; });
    return m;
  }, []);

  /* rAF-throttled cursor tracking for the preview panel */
  const onMove = useCallback(e => {
    if (!hasHover) return;
    posRef.current = { x: e.clientX, y: e.clientY };
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      setPos(posRef.current);
    });
  }, [hasHover]);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  const handleEnter = useCallback(p => { setHovered(p); setShown(true); }, []);
  const handleLeave = useCallback(() => setShown(false), []);

  return (
    <section
      onMouseMove={onMove}
      style={{ padding: 'clamp(28px,3.5vw,48px) clamp(20px,5vw,48px) clamp(48px,6vw,80px)', position: 'relative' }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>

        {/* header */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 22, flexWrap: 'wrap', marginBottom: 'clamp(22px,2.6vw,34px)',
        }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color: 'var(--blue)', textTransform: 'uppercase' }}>
              FULL ARCHIVE
            </span>
            <h2 style={{
              fontFamily: "'Anton',sans-serif", fontWeight: 400, color: 'var(--ink)',
              fontSize: 'clamp(26px,3.6vw,46px)', lineHeight: 1.06, marginTop: 10,
            }}>
              EVERY PROJECT,{' '}
              <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'var(--red)' }}>
                on the record
              </span>
            </h2>
          </div>

          <div style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
            color: 'rgba(10,10,20,0.4)', paddingBottom: 6,
          }}>
            [ {String(list.length).padStart(2, '0')} / {String(PROJECTS.length).padStart(2, '0')} ]
          </div>
        </div>

        {/* filter — underlined text switches */}
        <div className="pj-filter" style={{
          display: 'flex', gap: 'clamp(14px,2.4vw,30px)', flexWrap: 'nowrap',
          overflowX: 'auto', paddingBottom: 14, marginBottom: 4,
          borderBottom: '1px solid var(--line)',
        }}>
          {PROJECT_SECTORS.map(s => {
            const on = filter === s.slug;
            const tint = s.slug === 'all' ? 'var(--ink)' : s.color;
            return (
              <button
                key={s.slug}
                onClick={() => setFilter(s.slug)}
                aria-pressed={on}
                className="hoverable"
                style={{
                  position: 'relative', flexShrink: 0, whiteSpace: 'nowrap',
                  padding: '4px 0 14px', background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 'clamp(12px,1.3vw,14.5px)', fontWeight: 600,
                  letterSpacing: '0.02em',
                  color: on ? tint : 'rgba(10,10,20,0.45)',
                  transition: 'color 260ms ease',
                }}
                onMouseEnter={e => { if (!on) e.currentTarget.style.color = 'var(--ink)'; }}
                onMouseLeave={e => { if (!on) e.currentTarget.style.color = 'rgba(10,10,20,0.45)'; }}
              >
                {s.name}
                <sup style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                  marginLeft: 5, color: on ? tint : 'rgba(10,10,20,0.32)',
                }}>{counts[s.slug] || 0}</sup>
                <span aria-hidden style={{
                  position: 'absolute', left: 0, right: 0, bottom: -1, height: 2,
                  background: s.slug === 'all' ? 'var(--ink)' : s.color,
                  transform: on ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'left',
                  transition: 'transform 340ms cubic-bezier(.2,.8,.2,1)',
                }} />
              </button>
            );
          })}
        </div>

        {/* rows */}
        <div style={{ borderBottom: '1px solid var(--line)' }}>
          {list.map((p, i) => (
            <IndexRow
              key={p.slug}
              p={p}
              n={i + 1}
              narrow={narrow}
              onEnter={handleEnter}
              onLeave={handleLeave}
            />
          ))}
        </div>
      </div>

      {hasHover && <IndexPreview project={hovered} pos={pos} shown={shown} />}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   IMPACT BAND
═══════════════════════════════════════════════════════════ */

function ImpactBand() {
  const [ref, visible] = useRevealOnce(0.25);
  return (
    <section
      ref={ref}
      style={{
        background: 'linear-gradient(150deg, #0A0A14 0%, #101024 55%, #0A0A14 100%)',
        padding: 'clamp(48px,6vw,80px) clamp(20px,5vw,48px)',
        position: 'relative', overflow: 'hidden',
      }}
    >
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.022) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.022) 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }} />
      <div aria-hidden style={{
        position: 'absolute', top: '-30%', left: '8%', width: '45%', height: '110%',
        background: 'radial-gradient(ellipse, rgba(0,60,255,0.22) 0%, transparent 62%)',
      }} />
      <div aria-hidden style={{
        position: 'absolute', bottom: '-40%', right: '4%', width: '42%', height: '120%',
        background: 'radial-gradient(ellipse, rgba(255,0,4,0.15) 0%, transparent 62%)',
      }} />

      <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 'clamp(32px,4vw,52px)' }}>
          <span style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(255,255,255,0.42)' }}>
            CUMULATIVE IMPACT
          </span>
          <h2 style={{
            fontFamily: "'Anton',sans-serif", fontWeight: 400, color: 'white',
            fontSize: 'clamp(28px,4vw,52px)', lineHeight: 1.06, marginTop: 12,
          }}>
            Systems in the field,{' '}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#FF6B72' }}>not in a deck</span>
          </h2>
        </div>

        <div className="pj-impact-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, background: 'rgba(255,255,255,0.09)', borderRadius: 18, overflow: 'hidden' }}>
          {PROJECT_IMPACT.map((s, i) => <ImpactCell key={s.label} s={s} i={i} active={visible} />)}
        </div>
      </div>
    </section>
  );
}

function ImpactCell({ s, i, active }) {
  const v = useCount(s.target, active, 1800 + i * 160);
  return (
    <div style={{ background: '#0A0A14', padding: 'clamp(22px,2.6vw,34px) clamp(16px,2vw,26px)', textAlign: 'center' }}>
      <div style={{
        fontFamily: "'Anton',sans-serif", fontWeight: 400, lineHeight: 1,
        fontSize: 'clamp(34px,4.4vw,58px)',
        background: 'linear-gradient(135deg, #FFFFFF 0%, #9BA6FF 100%)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
      }}>
        {v}{s.suffix}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.86)', marginTop: 10 }}>{s.label}</div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.34)', marginTop: 3 }}>{s.sub}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */

export default function ProjectsPage() {
  const [heroRef, heroVisible] = useRevealOnce(0.05);
  const [techRef, techVisible] = useRevealOnce(0.15);

  const featured = useMemo(() => PROJECTS.filter(p => p.featured), []);
  const techLine = [...PROJECT_TECH, ...PROJECT_TECH].join('   ·   ');

  return (
    <>
      <Helmet>
        <title>Projects &amp; Case Studies — Zone-X Engineering Work</title>
        <meta
          name="description"
          content="Selected Zone-X engineering work across healthcare, utilities & energy, industrial IoT and education — full case studies with architecture, delivery timelines and measured outcomes."
        />
        <link rel="canonical" href="https://zone-x.in/projects" />
      </Helmet>

      {/* ══════════════ HERO ══════════════ */}
      <section ref={heroRef} style={{ paddingTop: 120, paddingBottom: 0, position: 'relative', overflow: 'hidden' }}>
        <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'radial-gradient(rgba(10,10,20,0.032) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div aria-hidden style={{ position: 'absolute', top: -140, right: '2%', width: 640, height: 640, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,60,255,0.09) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <div aria-hidden style={{ position: 'absolute', bottom: -100, left: '1%', width: 460, height: 460, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,0,4,0.07) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)', position: 'relative', zIndex: 1 }}>
          <div className="pj-hero-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.06fr) minmax(0,1fr)', gap: 'clamp(32px,4.5vw,64px)', alignItems: 'center' }}>

            {/* left */}
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px',
                background: 'rgba(255,0,4,0.07)', border: '1px solid rgba(255,0,4,0.2)',
                borderRadius: 999, marginBottom: 30,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--red)' }} />
                <span style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--red)' }}>
                  {PROJECTS.length} CASE STUDIES
                </span>
              </div>

              <h1 style={{
                fontFamily: "'Anton',sans-serif", fontWeight: 400,
                fontSize: 'clamp(46px,7.4vw,98px)', lineHeight: 0.95, color: 'var(--ink)', margin: 0,
              }}>
                Selected<br />
                <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'var(--red)' }}>
                  Work
                </span>
              </h1>

              <p style={{
                fontSize: 'clamp(14px,1.35vw,16.5px)', lineHeight: 1.84, color: 'var(--ink-soft)',
                maxWidth: 500, marginTop: 24, marginBottom: 40,
              }}>
                Every project here is a system that runs in the real world — in wards, on plant floors, across
                solar rows and in teaching labs. Each case study shows the constraint we started from, the
                architecture we shipped, and what changed once it was live.
              </p>

              <div className="pj-hero-stats" style={{
                display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 'clamp(16px,2.4vw,32px)',
                paddingBottom: 46, borderBottom: '1px solid rgba(10,10,20,0.08)',
              }}>
                {PROJECT_IMPACT.map(s => <HeroStat key={s.label} {...s} active={heroVisible} />)}
              </div>
            </div>

            {/* right */}
            <div className="pj-hero-viz" style={{
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? 'none' : 'translateY(26px) scale(0.95)',
              transition: 'opacity 900ms 200ms cubic-bezier(.2,.8,.2,1), transform 900ms 200ms cubic-bezier(.2,.8,.2,1)',
            }}>
              <TelemetryConsole />
            </div>
          </div>
        </div>

        {/* project ticker */}
        <div style={{ overflow: 'hidden', borderTop: '1px solid rgba(10,10,20,0.07)', padding: '15px 0', background: 'rgba(10,10,20,0.02)', marginTop: 'clamp(32px,4vw,56px)' }}>
          <div style={{ display: 'flex', animation: 'pjTicker 30s linear infinite', whiteSpace: 'nowrap', willChange: 'transform' }}>
            {[0, 1].map(k => (
              <span key={k} style={{ display: 'inline-flex', paddingRight: 40 }}>
                {PROJECTS.map(p => (
                  <span key={`${k}-${p.slug}`} style={{
                    fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(12px,1.7vw,17px)',
                    letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(10,10,20,0.17)',
                    paddingRight: 30, display: 'inline-flex', alignItems: 'center', gap: 30,
                  }}>
                    {p.title}
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: hexA(p.color, 0.5) }} />
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ FEATURED ══════════════ */}
      <FeaturedSpotlight items={featured} />

      {/* ══════════════ THE INDEX ══════════════ */}
      <WorkIndex />

      {/* ══════════════ IMPACT ══════════════ */}
      <ImpactBand />

      {/* ══════════════ TECH MARQUEE ══════════════ */}
      <section ref={techRef} style={{ padding: 'clamp(44px,5vw,68px) 0', overflow: 'hidden', background: 'var(--paper)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)', marginBottom: 26 }}>
          <span style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color: 'var(--blue)', textTransform: 'uppercase' }}>
            ENGINEERING SURFACE
          </span>
          <h2 style={{
            fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(24px,3.2vw,40px)',
            lineHeight: 1.08, color: 'var(--ink)', marginTop: 10, maxWidth: 620,
            opacity: techVisible ? 1 : 0, transform: techVisible ? 'none' : 'translateY(18px)',
            transition: 'opacity 600ms ease, transform 600ms ease',
          }}>
            Silicon to dashboard —{' '}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: 'var(--red)' }}>one team, one stack</span>
          </h2>
        </div>

        <div className="pj-marquee" style={{ overflow: 'hidden', maskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)' }}>
          <div style={{ display: 'flex', whiteSpace: 'nowrap', animation: 'pjTicker 42s linear infinite', willChange: 'transform' }}>
            {[0, 1].map(k => (
              <span key={k} style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 'clamp(13px,1.5vw,17px)',
                color: 'rgba(10,10,20,0.34)', paddingRight: 44, letterSpacing: '0.02em',
              }}>{techLine}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ CTA ══════════════ */}
      <section style={{ padding: 'clamp(56px,6vw,88px) clamp(20px,5vw,48px)' }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto', borderRadius: 26, overflow: 'hidden', position: 'relative',
          background: 'linear-gradient(135deg, #0A0A14 0%, #16162E 100%)',
          padding: 'clamp(36px,4.6vw,64px)',
        }}>
          <div aria-hidden style={{
            position: 'absolute', top: '-45%', right: '-8%', width: '55%', height: '160%',
            background: 'radial-gradient(ellipse, rgba(0,60,255,0.3) 0%, transparent 62%)',
          }} />
          <div aria-hidden style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '24px 24px',
          }} />

          <div className="pj-cta-inner" style={{
            position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 'clamp(24px,3vw,48px)', flexWrap: 'wrap',
          }}>
            <div style={{ maxWidth: 640 }}>
              <span style={{ fontWeight: 700, fontSize: 10.5, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>
                START A PROJECT
              </span>
              <h2 style={{
                fontFamily: "'Anton',sans-serif", fontWeight: 400, color: 'white',
                fontSize: 'clamp(28px,4.2vw,54px)', lineHeight: 1.05, marginTop: 12,
              }}>
                Tell us the constraint.<br />
                <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#FF6B72' }}>
                  We&rsquo;ll show you the system.
                </span>
              </h2>
              <p style={{ fontSize: 'clamp(13.5px,1.3vw,15.5px)', lineHeight: 1.8, color: 'rgba(255,255,255,0.62)', marginTop: 16 }}>
                Every engagement above started as a problem somebody had lived with for years. Bring us yours and
                we&rsquo;ll scope it honestly — including when the answer is that you do not need us.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Link
                to="/contact"
                className="hoverable"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                  padding: '17px 32px', borderRadius: 999, whiteSpace: 'nowrap',
                  background: 'linear-gradient(135deg, var(--red), #cc0003)', color: 'white',
                  fontSize: 14.5, fontWeight: 700, boxShadow: '0 16px 40px rgba(255,0,4,0.42)',
                  transition: 'transform 220ms ease, box-shadow 220ms ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 22px 52px rgba(255,0,4,0.55)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(255,0,4,0.42)'; }}
              >
                Start a conversation <ArrowIcon />
              </Link>
              <Link
                to="/sectors"
                className="hoverable"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '15px 32px', borderRadius: 999, whiteSpace: 'nowrap',
                  border: '1px solid rgba(255,255,255,0.22)', color: 'rgba(255,255,255,0.86)',
                  fontSize: 13.5, fontWeight: 600, transition: 'background 220ms ease, border-color 220ms ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; }}
              >
                Explore sectors
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes pjTicker   { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes pjBlink    { 0%,50% { opacity: 1; } 51%,100% { opacity: 0; } }
        @keyframes pjScan     { 0% { top: 4%; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { top: 96%; opacity: 0; } }
        @keyframes pjKenBurns { from { transform: scale(1) translate(0,0); } to { transform: scale(1.08) translate(-1.5%, -1.5%); } }
        @keyframes pjFadeSwap { from { opacity: 0.35; } to { opacity: 1; } }

        .pj-filter { scrollbar-width: none; -ms-overflow-style: none; }
        .pj-filter::-webkit-scrollbar { display: none; }
        .pj-row:focus-visible { outline: 2px solid var(--blue); outline-offset: -2px; }

        @media (max-width: 900px) {
          .pj-hero-grid   { grid-template-columns: 1fr !important; }
          .pj-hero-viz    { display: none !important; }
          .pj-spotlight   { grid-template-columns: 1fr !important; }
          .pj-impact-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 640px) {
          .pj-hero-stats { grid-template-columns: repeat(2,1fr) !important; row-gap: 26px !important; }
          .pj-cta-inner  { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </>
  );
}
